import React, { useCallback, useEffect, useRef, useState } from 'react'
import cx from 'classnames'

import { animate, motion, useMotionValue, AnimatePresence } from 'framer-motion'

import { useMediaQuery } from '../core/hooks'
import { isDesktop } from '../../../services/device'
import { useLazyGesture } from './useLazyGesture'
import { useInView } from 'react-intersection-observer'
import { IKImage } from 'imagekitio-react'

import { OverlayTrigger, Popover } from 'react-bootstrap'
import { ImageView, VideoView } from '../background'

import { ReactSVG } from 'react-svg'
import { ParallaxProvider } from 'react-scroll-parallax'
import { EmbedContext, GatingContext } from '../core/context'
import { useDesktop as useDesktopHook } from '../spatialContent'

import styles from './interactive.module.scss'

import DownloadButton from './DownloadButton'
import debounce from 'lodash/debounce';
import get from 'lodash/get';
import has from 'lodash/has';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';
import size from 'lodash/size';


const MenuIcon = '/images/interactive/menu.svg'
const ArrowIcon = '/images/interactive/arrow.svg'
const UpArrowIcon = '/images/interactive/up-arrow.svg'
const DownArrowIcon = '/images/interactive/down-arrow.svg'

const transition = {
  ease: 'easeOut',
  bounce: 0,
  duration: 0.2
}

const openSpring = { type: 'spring', stiffness: 200, damping: 30 }
const closeSpring = { type: 'spring', stiffness: 300, damping: 35 }

const TOP_OFFSET = 50
const LEFT_OFFSET = 60
const SCROLL_THRESHOLD = 10
let initialHeightSet = false;

const measurements = {
  topOffset: (desktop, value) => {
    if (!desktop) {
      return 0
    }

    const ratio = value / TOP_OFFSET
    return TOP_OFFSET * (1 - ratio)
  },
  leftOffset: (desktop, value) => {
    if (!desktop) {
      return 20
    }

    const ratio = value / TOP_OFFSET
    return LEFT_OFFSET * (1 - ratio)
  },
  borderRadius: (desktop, value) => {
    const radius = desktop ? 30 : 15
    const ratio = value / TOP_OFFSET
    return radius * (1 - ratio)
  },
  arrowOffset: (desktop, value) => {
    const ratio = value / TOP_OFFSET
    const width = 45
    const offset = LEFT_OFFSET + 0.5 * width
    return offset * ratio
  }
}

/**
 * Memo function equal common function
 */
function memoFunctionEqual(paths) {
  return function (prevProps, nextProps) {
    const next = pick(nextProps, paths)
    const previous = pick(prevProps, paths)

    const equals = isEqual(next, previous)
    return equals
  }
}

export const useDesktop = () => {
  const [desktop, setDesktop] = useState(false)
  const [mounted, setMounted] = useState(false)
  const isDesktopQuery = useMediaQuery('(min-width: 992px)')

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (mounted) {
      setDesktop(isDesktop() && isDesktopQuery)
    }
  }, [isDesktopQuery, mounted])

  return mounted ? desktop : false
}

export const CarouselContext = React.createContext({
  initialised: false,
  navigateToPage: (index, fullscreen) => { },
  navigatePreviousPage: () => { },
  navigateNextPage: () => { },
  scrollToTop: () => { }
})

export const CarouselNavigationContext = React.createContext({
  scrollToTop: () => { },
  scrollToBottom: () => { },
  scrollToWidget: () => { }
})

export function InteractiveContent({
  children,
  initialPage,
  appearance,
  downloadUrl,
  onChange,
  onDownload,
}) {
  const pages = size(children)

  if (pages <= 0) {
    return false
  }

  const [isEmbedded, setIsEmbedded] = useState(false)
  useEffect(() => {
    const handleIcWindowHeight = (event) => {
      if (event.data && event.data.type === 'cleverstory:embed:windowHeightData') {
        setIsEmbedded(true)
        setTimeout(() => initialHeightSet = true)
      }
    };
    window.addEventListener('message', handleIcWindowHeight);

    const requestWindowHeight = () => {
      if (window.parent && window.parent !== window) {
        window.parent.postMessage({
          type: 'cleverstory:embed:requestWindowHeight',
          source: 'cleverstory-ic-content'
        }, '*');
      }
    };
    requestWindowHeight();
    return () => {
      window.removeEventListener('message', handleIcWindowHeight);
    };
  }, []);


  return (
    <Carousel
      initialPage={initialPage}
      downloadUrl={downloadUrl}
      appearance={appearance}
      onChange={onChange}
      onDownload={onDownload}
      isEmbedded={isEmbedded}
    >
      {children}
    </Carousel>
  ) 
}

function Carousel({
  children,
  initialPage,
  appearance,
  downloadUrl,
  onChange,
  onDownload,
  isEmbedded,
}) {
  const containerRef = React.useRef(null);
  const wheelLockRef = useRef(false);

  const pages = size(children)
  const singlePage = pages == 1

  const desktop = useDesktopHook()
  const x = useMotionValue(0)
  const [y, setY] = React.useState(singlePage ? TOP_OFFSET : 0)
  const [fullScreen, setFullScreen] = React.useState(singlePage)
  const [scrollPage, setScrollPage] = useState({
    progress: 0,
    scrollTop: 0
  })

  const [initialised, setInitialised] = React.useState(false)

  const [activeNavigation, setActiveNavigation] = useState('')

  const [activePage, setActivePage] = React.useState({
    index: initialPage,
    fullscreen: singlePage
  })

  const activePageRef = React.useRef(activePage);

  const [pagesMetadata, setPagesMetadata] = React.useState([])

  const [scale, setScale] = React.useState(0.9)

  const gatingContext = React.useContext(GatingContext)
  const gatingConfigurations = get(gatingContext, 'configurations', [])

  const embedContext = React.useContext(EmbedContext);

  let actionScrolling = useRef(false);
  const desktopRef = useRef(desktop)

  useEffect(() => {
    desktopRef.current = desktop
  }, [desktop])

  const calculateNewX = () => {
    if (!desktop) {
      return 0
    }

    const left = measurements.leftOffset(desktop, y)
    const offset =
      -activePage.index * (containerRef.current?.clientWidth - 105 || 0) -
      (LEFT_OFFSET - left)
    return offset
  }

  const [carouselState, setCarouselState] = useState({
    initialised: initialised,
    activePage: activePage,
    pages: pages,
    navigateToPage: () => { }
  });

  const [carouselNavigationState, setCarouselNavigationState] = useState({
    scrollToTop: () => { },
    scrollToBottom: () => { },
    scrollToWidget: () => { }
  })

  React.useEffect(() => {
    activePageRef.current = activePage;
  }, [activePage]);

  React.useEffect(() => {
    setCarouselNavigationState((state) => {
      return {
        ...state,
        scrollToTop: handleScrollTop,
        scrollToBottom: handleScrollBottom,
        scrollToWidget: handleScrollToWidget
      }
    });

    setCarouselState((state) => {
      return {
        ...state,
        navigateToPage: navigateToPage
      }
    });
  }, [])

  React.useEffect(() => {
    setCarouselState((state) => {
      return {
        ...state,
        initialised: initialised,
        activePage: activePage,
        pages: pages
      }
    })
  }, [initialised, activePage, pages])

  React.useEffect(() => {
    if (!desktop && activePage){
      const pageElement = document.getElementById(`page_${activePage.index}`);
      if (pageElement){
        pageElement.scrollIntoView({ behavior: 'smooth' });
      }
    }
  },[desktop])

  React.useEffect(() => {
    if(initialised && embedContext.embedded) {
      var isInIframe = window.parent !== window;
      if (isInIframe) {
        const parentWindow = window.parent;
        parentWindow.postMessage({
          type: "cleverstory:embed:page:loading",
          payload: {
            id: activePage.index
          }
        }, '*');
      }
    }
  }, [initialised]);

  React.useEffect(() => {
    const offset = desktop ? 120 : 40;
  
    const scale = (window.innerWidth - offset) / window.innerWidth
    setScale(scale);

    document.addEventListener('fullscreenchange', exitHandler)
    document.addEventListener('webkitfullscreenchange', exitHandler)
    document.addEventListener('mozfullscreenchange', exitHandler)
    document.addEventListener('MSFullscreenChange', exitHandler)
    document.querySelector(`.${styles.carousel}`).addEventListener(
      'touchmove',
      (e) => {
        e.preventDefault()
      },
      false
    )

    const pagesMetadata = children.map((child) => {
      const name = get(child, 'props.widgetProps.name')
      const description = get(
        child,
        'props.widgetProps.properties.desktop.seo.description'
      )
      const icon = get(child, 'props.widgetProps.icon.url')

      return {
        name: name,
        description: description,
        icon: icon
      }
    })

    setPagesMetadata(pagesMetadata)

    setTimeout(() => {
      setInitialised(true)
    }, 1000)
  }, []);

  React.useEffect(() => {
    const controls = animate(x, calculateNewX(), transition)

    triggerSectionGating()

    return () => {
      controls.stop()
    }
  }, [activePage.index])

  React.useEffect(() => {
    if (gatingConfigurations.length > 0) {
      triggerSectionGating()
    }
  }, [gatingConfigurations])

  React.useEffect(() => {
    const controls = animate(x, calculateNewX(), {
      duration: 0
    })
    return () => {
      controls.stop()
    }
  }, [desktop])

  useEffect(() => {
    handleWidowResize();
  }, [desktop]);

  React.useEffect(() => {
    window.addEventListener('resize', handleWidowResize);

    calculateWindowHeight();
    return () => {
      window.removeEventListener('resize', handleWidowResize)
    }
  }, [desktop, activePage.index])

  React.useEffect(() => {
    if (!activePage.fullscreen && y > LEFT_OFFSET / 2) {
      setActivePage((state) => {
        return {
          ...state,
          fullscreen: true
        }
      })
    } else if (activePage.fullscreen && y < LEFT_OFFSET / 2) {
      setActivePage((state) => {
        return {
          ...state,
          fullscreen: false
        }
      })
    }

    const controls = animate(x, calculateNewX(), transition)
    return controls.stop
  }, [y])

  React.useEffect(() => {
    const newY = activePage.fullscreen ? TOP_OFFSET : 0
    setY(newY)
  }, [activePage.fullscreen])

  const triggerSectionGating = () => {
    if (gatingContext.preview) {
      return
    }

    var sectionConfigurations = gatingConfigurations.filter(
      (item) => get(item, 'trigger.type', '') === 'INTERACTIVE_SECTION'
    )

    sectionConfigurations.every((item) => {
      var section = get(item, 'trigger.value', 0)

      if (section <= activePage.index + 1) {
        var gating = get(item, 'gating.id', null)

        if (gating) {
          gatingContext.triggerGating('INTERACTIVE_SECTION', null, item)
          return false
        }
      }

      return true
    })
  }

  const handleEndDrag = (e, dragProps) => {
    if (!dragProps) {
      return
    }

    const clientWidth = containerRef.current?.clientWidth - 100 || 0

    const { offset, velocity } = dragProps

    if (Math.abs(velocity.y) > Math.abs(velocity.x)) {
      animate(x, calculateNewX(), transition)
      return
    }

    if (offset.x > clientWidth / 4) {
      navigateToPage(Math.max(0, activePage.index - 1), false)
    } else if (offset.x < -clientWidth / 4) {
      navigateToPage(Math.min(pages - 1, activePage.index + 1), false)
    } else {
      animate(x, calculateNewX(), transition)
    }
  }

  const handleGuesture = (offset, direction = 'vertical') => {
    if (!desktop || singlePage) {
      return;
    }

    if (direction === 'horizontal') {
      return;
    }

    if (activePage.fullscreen) {
      const activePageRef = containerRef.current.querySelector(
        `.${styles.page}.${styles.fullscreen}`
      )
      if (activePageRef) {
        const scrollTop = activePageRef.scrollTop
        if (scrollTop > 0) {
          return
        }
      }
    }

    setY((y) => {
      var newY = y + offset
      newY = Math.min(newY, TOP_OFFSET)
      newY = Math.max(newY, 0)

      return newY
    })
  }

  const navigateToPage = (index, fullscreen) => {
    if (gatingContext.configuration) {
      return;
    }

    const previousIndex = activePageRef.current.index;
    if (index < 0) {
      return;
    }

    if (index >= pages) {
      return;
    }

    if (previousIndex !== index) {
      const pageElement = containerRef.current.querySelector(`#page_${index}.interactive_content__page`);
      if (pageElement) {
        // Reset the scroll position for the page
        pageElement.scrollTop = 0;
      }
    }

    setActivePage((state) => ({
      ...state,
      index: index,
      fullscreen: pages > 1 ? fullscreen : true,
    }));

    if (onChange && previousIndex !== index) {
      onChange('index', {
        index: index,
      });
    }

    var duration = 300;
    const diff = Math.abs(index - previousIndex);
    if (diff > 0) {
      duration = Math.abs(index - previousIndex) * 500;
    }

    return new Promise((resolve) => setTimeout(resolve, duration));
  }
  const handleWheel = useCallback((data) => {
    const { movement, event } = data;
    const isHorizontalWheel = Math.abs(event.deltaX) > Math.abs(event.deltaY);

    if (!isHorizontalWheel || Math.abs(event.deltaY) > 1) {
      handleGuesture(movement[1], 'vertical');
      return;
    }

    if (wheelLockRef.current) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    const isTouchpad = Math.abs(event.deltaX) < 20;
    if (isTouchpad) {
      event.stopPropagation();
      return;
    }

    wheelLockRef.current = true;
    event.stopPropagation();
    event.preventDefault();

    if (event.deltaX > 0) {
      navigateToPage(activePage.index + 1, activePage.fullscreen);
    } else if (event.deltaX < 0) {
      navigateToPage(activePage.index - 1, activePage.fullscreen);
    }

    setTimeout(() => {
      wheelLockRef.current = false;
    }, 800);
  }, [activePage, handleGuesture, navigateToPage]);

  useLazyGesture(
    containerRef, 
    {
      onDrag: (data) => {
        const { movement } = data;
        handleGuesture(-movement[1], 'vertical');
      },
      onWheel: handleWheel
    }, 
    {
      eventOptions: { passive: false }
    }
  );
  const handleWidowResize = () => {
    calculateWindowHeight()

    const offset = desktop || window.innerWidth >= 1024 ? 120 : 40
    const scale = (window.innerWidth - offset) / window.innerWidth
    setScale(scale)

    animate(x, calculateNewX(), {
      duration: 0
    })
  }

  const calculateWindowHeight = () => {
    if (initialHeightSet) {
      document.documentElement.style.setProperty(
        '--window-height',
        `${window.innerHeight}px`
      )
    }
  }

  const handleLeftArrowClick = useCallback(() => {
    navigateToPage(activePage.index - 1, false)
  }, [activePage.index])

  const handleRightArrowClick = useCallback(() => {
    navigateToPage(activePage.index + 1, false)
  }, [activePage.index])

  const handleMenuChanage = (page) => {
    navigateToPage(page, false)
  }

  const handleNavigatePage = () => {
    const desktop = desktopRef.current;
    const selector = desktop ? `.${styles.fullscreen}` : `.${styles.body}`
    const scrollContainer = desktop
      ? containerRef.current.querySelector(selector)
      : document
        .getElementById(`page_${activePageRef.current.index}`)
        .querySelector(selector)

    if (scrollContainer.scrollTop === 0) {
      navigateToPage(activePageRef.current.index, singlePage)
      scrollContainer.removeEventListener('scroll', handleNavigatePage)
    }
  }

  const handleScrollTop = () => {
    const desktop = desktopRef.current;
    const selector = desktop ? `.${styles.fullscreen}` : `.${styles.body}`
    const scrollContainer = desktop
      ? containerRef.current.querySelector(selector)
      : document
        .getElementById(`page_${activePageRef.current.index}`)
        .querySelector(selector)
    scrollContainer.addEventListener('scroll', handleNavigatePage)
    scrollContainer.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth'
    })
  }

  const handleScrollBottom = () => {
    const desktop = desktopRef.current;
    navigateToPage(activePageRef.current.index, true).then(() => {
      const selector = desktop ? `.${styles.fullscreen}` : `.${styles.body}`
      const scrollContainer = desktop
        ? containerRef.current.querySelector(selector)
        : document
          .getElementById(`page_${activePageRef.current.index}`)
          .querySelector(selector)

      const height = scrollContainer.scrollHeight
      scrollContainer.scroll({ top: height, left: 0, behavior: 'smooth' })
    })
  }

  const handleScrollDown = () => {
    navigateToPage(activePageRef.current.index, true).then(() => {
      const scrollContainer = containerRef.current.querySelector(
        `.${styles.fullscreen}`
      )
      const height = scrollContainer.clientHeight
      scrollContainer.scroll({ top: height, left: 0, behavior: 'smooth' })
    })
  }

  const exitHandler = () => {
    if (
      !document.fullscreenElement &&
      !document.webkitIsFullScreen &&
      !document.mozFullScreen &&
      !document.msFullscreenElement
    ) {
      setFullScreen(false)
    }
  }

  const handleMouseMove = (event) => {
    // if (typeof window === 'undefined') {
    //   return
    // }
    if (!initialHeightSet) { initialHeightSet = true }
    switch (true) {
      case event.clientY > (80 / 100) * window.innerHeight &&
        (event.clientX > (50 / 100) * window.innerWidth ||
          event.clientX < (50 / 100) * window.innerWidth):
        if (activeNavigation !== 'bottom') {
          setActiveNavigation('bottom')
        }
        break

      case event.clientX > (50 / 100) * window.innerWidth:
        if (activeNavigation !== 'right') {
          setActiveNavigation('right')
        }
        break

      case event.clientX < (50 / 100) * window.innerWidth:
        if (activeNavigation !== 'left') {
          setActiveNavigation('left')
        }
        break

      default:
        setActiveNavigation('')
        break
    }
  }

  const handleFullScreen = () => {
    const viewerContainer = document.getElementsByTagName(`body`)[0]
    setFullScreen(!fullScreen)

    if (document.fullscreenElement || document.webkitFullscreenElement) {
      document.exitFullscreen
        ? document.exitFullscreen()
        : document.webkitExitFullscreen()
    }

    if (viewerContainer.requestFullScreen) {
      viewerContainer.requestFullScreen()
    } else if (viewerContainer.mozRequestFullScreen) {
      viewerContainer.mozRequestFullScreen()
    } else if (viewerContainer.webkitRequestFullScreen) {
      viewerContainer.webkitRequestFullScreen()
    }
  }

  const handleScrollToWidget = (widget) => {
    const desktop = desktopRef.current;
    
    if (!widget) {
      return false
    }

    const { value, type } = widget;

    var widgetElement = document.getElementById(value)
    if (!widgetElement) {
      return false
    }

    const toScrollItem = widgetElement.closest('.interactive_content__page')
    var pageId = toScrollItem.id
    pageId = pageId.split('_')
    var index = pageId[1]
    index = parseInt(index < 0 ? 0 : index)

    actionScrolling.current = true;

    navigateToPage(index, true).then(() => {
      setTimeout(() => {
        document.querySelector(`.${styles.carousel}`).scrollTop = 0;
        actionScrolling.current = false;
      }, 100);

      const rect = widgetElement.getBoundingClientRect();

      const selector = desktop ? `.${styles.fullscreen}` : `.${styles.body}`
      const scrollContainer = desktop
        ? containerRef.current.querySelector(selector)
        : document.querySelector(`#page_${activePageRef?.current?.index}`)?.querySelector(selector);
  
      const distanceToTop = rect.top + scrollContainer?.scrollTop || 0;

      if (type === 'carousel') {
        const observer = new IntersectionObserver(
          ([entry]) => {
            if (!entry.isIntersecting) {

              const screenPosition = 200; // screen position from top
              
              scrollContainer?.scroll({
                top: distanceToTop - screenPosition <= 0 ? 0 : distanceToTop - screenPosition,
                left: 0,
                behavior: 'smooth'
              })
            }

            observer.disconnect() // stop after first check
          },
          {
            threshold: 0 // partially visible check
          }
        )

        observer.observe(widgetElement)

        return;
      }

      if (!desktop) {
        widgetElement.scrollIntoView({ behavior: 'smooth' });
        return;
      }

      scrollContainer?.scroll({
        top: distanceToTop,
        left: 0,
        behavior: 'smooth'
      });
    })
  }
  

  return (
    <CarouselContext.Provider value={carouselState}>
      <CarouselNavigationContext.Provider value={carouselNavigationState}>
        {!initialised && (
          <div className={styles.loading}>
            <div className={styles.loader}>
              <video loop autoPlay playsInline muted>
                <source src='/images/shortLoader.mp4' type='video/mp4' />
              </video>
              <label>Loading Interactive content...</label>
            </div>
          </div>
        )}
        <motion.div
          ref={containerRef}
          style={{ '--window-height': isEmbedded ? '815px' : '100vh' }}
          className={cx(styles.carousel, {
            [styles.singlePage]: singlePage,
          })}
        >
          {appearance && (
            <React.Fragment>
              <div
                className={cx(
                  styles.overlay,
                  { [styles.lightOverlay]: appearance.overlay === 'light' },
                  { [styles.darkOverlay]: appearance.overlay === 'dark' }
                )}
              />
              <BackgroundManager background={appearance.background} />
            </React.Fragment>
          )}

          <KeyManager>
            {!singlePage && (
              <Arrow
                direction='left'
                offset={scrollPage.scrollTop}
                activePage={activePage}
                show={
                  activeNavigation == 'left' &&
                  scrollPage.scrollTop <= window.innerHeight
                }
                disabled={activePage.index <= 0 || activeNavigation !== 'left'}
                onClick={handleLeftArrowClick}
              />
            )}

            {!singlePage && (
              <Menu
                pages={pagesMetadata}
                offset={y}
                color={get(appearance, 'iconColor', {})}
                active={activePage.index}
                onChange={handleMenuChanage}
              />
            )}

            <motion.div
              className={cx(styles.rightMenu, {
                [styles.activePageMenu]: activePage.fullscreen
              })}
              animate={{
                transform: `translate3D(0, -${y}px, 0)`
              }}
              transition={transition}
            >
              {appearance.disableDownload === false && downloadUrl && (
                <DownloadButton
                  iconColor={get(appearance, 'iconColor', {})}
                  onDownload={onDownload}
                />
              )}
              <SearchPopup iconColor={get(appearance, 'iconColor', {})} />

              {desktop && (
                <FullScreenView
                  iconColor={get(appearance, 'iconColor', {})}
                  onFullScreen={handleFullScreen}
                  fullScreen={fullScreen}
                />
              )}
            </motion.div>

            <motion.div
              className={cx(styles.track)}
              style={{
                x
              }}
              initial={false}
              draggable
              drag={false}
              dragElastic={1}
              onDragEnd={handleEndDrag}
            >
              {children &&
                children.map((child, i) => {
                  const fullscreen =
                    activePage.index === i ? activePage.fullscreen : false
                  return (
                    <Page
                      key={i}
                      index={i}
                      active={activePage.index === i}
                      offset={activePage.index === i ? y : 0}
                      scale={scale}
                      fullscreen={fullscreen}
                      actionScrolling={actionScrolling}
                      handleMouseMove={handleMouseMove}
                      onDragEnd={handleEndDrag}
                      toggleFullScreen={(index, fullscreen) => {
                        navigateToPage(index, fullscreen)
                      }}
                      onScroll={(progress, scrollTop) => {
                        setScrollPage({
                          progress: progress,
                          scrollTop: scrollTop
                        })
                        onChange('progress', {
                          index: i,
                          progress: progress
                        })
                      }}
                      isEmbedded={isEmbedded}
                    >
                      {React.cloneElement(child)}
                    </Page>
                  )
                })}
            </motion.div>
            <Arrow
              direction='bottom'
              offset={y}
              activePage={activePage}
              show={activeNavigation === 'bottom' && !activePage.fullscreen}
              disabled={scrollPage.progress === 100}
              onClick={handleScrollDown}
            />

            {!singlePage && (
              <Arrow
                direction='right'
                offset={scrollPage.scrollTop}
                activePage={activePage}
                show={
                  activeNavigation === 'right' &&
                  scrollPage.scrollTop <= window.innerHeight
                }
                disabled={
                  activePage.index >= children.length - 1 ||
                  activeNavigation !== 'right'
                }
                onClick={handleRightArrowClick}
              />
            )}
          </KeyManager>
        </motion.div>
      </CarouselNavigationContext.Provider>
    </CarouselContext.Provider>
  )
}

/**
 * Page Component
 *
 */
function PageComponent({
  index,
  offset,
  scale,
  active,
  fullscreen,
  children,
  actionScrolling,
  toggleFullScreen,
  handleMouseMove,
  onScroll,
  isEmbedded
}) {
  const desktop = useDesktopHook()
  const pageRef = React.useRef(null)

  const carouselContext = React.useContext(CarouselContext)

  const [inViewRef, inView, entry] = useInView({
    threshold: [0, 1]
  })

  React.useEffect(() => {
    if (!entry) {
      return
    }

    if (desktop) {
      return
    }

    const intersectionRatio = entry.intersectionRatio
    if (intersectionRatio === 1 && !actionScrolling.current && !isEmbedded) {
      toggleFullScreen(index, fullscreen)
    }
  }, [entry, desktop, fullscreen, actionScrolling])

  // Trigger progress calculation on fullscreen, active
  React.useEffect(() => {
    calculateScrollPosition()
  }, [fullscreen, active, carouselContext.initialised])

  React.useEffect(() => {
    calculateScrollPosition()
    pageRef.current.addEventListener('scroll', handlePageScroll)
    pageRef.current.addEventListener('touchmove', function (event) {
      event.stopPropagation()
    })

    return () => {
      if (pageRef.current) {
        pageRef.current.removeEventListener('scroll', handlePageScroll)
      }
    }
  }, [pageRef, active, desktop, carouselContext.initialised])

  const setPageRef = useCallback(
    (node) => {
      pageRef.current = node
      inViewRef(node)
    },
    [inViewRef]
  )

  const radius = measurements.borderRadius(desktop, offset)
  const bottomRadius = desktop ? 0 : radius

  const top = measurements.topOffset(desktop, offset)
  const left = measurements.leftOffset(desktop, offset)
  const width = `calc(100vw - 2 * (${left}px))`
  var effectiveScale = fullscreen ? 1 : scale

  var pageStyle = {
    borderRadius: `${radius}px ${radius}px ${bottomRadius}px ${bottomRadius}px`,
    width: width
  }

  if (desktop) {
    pageStyle = {
      ...pageStyle,
      transform: `translate3D(0, calc(${top}px), 0)`
    }
  }

  const contentStyle = {
    transform: `scale(${effectiveScale})`
  }

  const handlePageScroll = () => {
    calculateScrollPosition()
  }

  const handleMobileScroll = (progress) => {
    if (!active) {
      return
    }
    onScroll(progress)
  }

  const calculateScrollPosition = () => {
    if (!pageRef.current) {
      return
    }

    if (!active) {
      return
    }

    if (!desktop) {
      return
    }

    if (!carouselContext.initialised) {
      return
    }
    const scrollTop = pageRef.current.scrollTop
    const clientHeight = pageRef.current.clientHeight
    const scrollHeight = pageRef.current.scrollHeight

    var percent = ((scrollTop + clientHeight) * 100) / scrollHeight
    percent = Math.max(0, percent)
    percent = Math.min(100, percent)
    onScroll(percent, scrollTop)
  }

  const handlePageClick = () => {
    if (desktop) {
      return false
    }

    if (toggleFullScreen) {
      toggleFullScreen(index, true)
    }
  }

  const handleClose = () => {
    if (desktop) {
      return false
    }

    if (toggleFullScreen) {
      toggleFullScreen(index, false)
    }
  }

  return (
    <motion.div
      ref={setPageRef}
      id={`page_${index}`}
      onMouseMove={debounce(handleMouseMove, 20)}
      key={`page_${index}_${desktop ? 'desktop' : 'mobile'}`}
      className={cx(styles.page, {
        [styles.fullscreen]: fullscreen,
        [styles.pageInMobile]: !active && !desktop,
        interactive_content__page: true
      })}
      transition={transition}
      animate={pageStyle}
      onClick={handlePageClick}
    >
      <CardPopup
        active={active}
        desktopPageRef={pageRef.current}
        open={fullscreen}
        onClose={handleClose}
        onScroll={handleMobileScroll}
      >
        <motion.div
          id={index}
          className={styles.content}
          transition={transition}
          initial={{
            transform: `scale(${scale})`
          }}
          animate={contentStyle}
        >
          {children}
        </motion.div>
      </CardPopup>
    </motion.div>
  )
}

const Page = React.memo(
  PageComponent,
  memoFunctionEqual([
    'index',
    'offset',
    'scale',
    'active',
    'fullscreen',
    'toggleFullScreen',
    'onScroll'
  ])
)

/**
 * Card popup in mobile
 */
function CardPopupComponent({
  active,
  open,
  desktopPageRef,
  children,
  onScroll,
  onClose
}) {
  const pageRef = React.useRef(null)

  const desktop = useDesktopHook()
  const y = useMotionValue(0)

  const carouselContext = React.useContext(CarouselContext);

  React.useEffect(() => {
    if (open) {
      document.querySelector('body').classList.add(styles.noScroll)
      if (pageRef.current) {
        pageRef.current.scrollTop = 0
      }
    } else {
      document.querySelector('body').classList.remove(styles.noScroll)
      y.set(0)
    }

    return () => { }
  }, [open])

  React.useEffect(() => {
    calculateScrollPosition()
  }, [active, open, carouselContext.initialised])

  React.useEffect(() => {
    pageRef.current.addEventListener('scroll', calculateScrollPosition)
    return () => {
      if (pageRef.current) {
        pageRef.current.removeEventListener('scroll', calculateScrollPosition)
      }
    }
  }, [pageRef, active, carouselContext.initialised])

  const calculateScrollPosition = () => {
    if (!pageRef.current) {
      return
    }

    if (desktop) {
      return
    }

    if (!active) {
      return
    }

    if (!carouselContext.initialised) {
      return
    }

    const scrollTop = pageRef.current.scrollTop
    const clientHeight = pageRef.current.clientHeight
    const scrollHeight = pageRef.current.scrollHeight

    var percent = ((scrollTop + clientHeight) * 100) / scrollHeight
    percent = Math.max(0, percent)
    percent = Math.min(100, percent)

    onScroll(percent)
  }
 
  return (
    <ParallaxProvider
      scrollContainer={desktop ? desktopPageRef : pageRef.current}
    >
      <motion.div
        className={cx(styles.card, {
          [styles.open]: !desktop && open
        })}
      >
        {carouselContext.pages > 1 && (
          <motion.div
            className={styles.close}
            animate={{
              x: open ? 0 : 100
            }}
          >
            <button
              className={styles.close}
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()

                if (onClose) {
                  onClose()
                }
              }}
            >
              <i className='cleverstory-close' />
            </button>
          </motion.div>
        )}
        <motion.div
          ref={pageRef}
          className={styles.body}
          style={{ y }}
          transition={open ? openSpring : closeSpring}
        >
          {children}
        </motion.div>
      </motion.div>
    </ParallaxProvider>
  )
}

const CardPopup = React.memo(
  CardPopupComponent,
  memoFunctionEqual(['active', 'open', 'onScroll', 'onClose'])
)

/**
 * Arrow component
 */
const ArrowComponent = ({
  direction,
  offset,
  disabled,
  show,
  onClick,
  activePage
}) => {
  // if (typeof window === 'undefined') {
  //   return null
  // }

  const [defaultNav, setDefaultNav] = React.useState(false);

  useEffect(() => {
    let defaultNav =
      activePage.index === 0 &&
      offset <= window.innerHeight &&
      direction === 'right';
    setDefaultNav(defaultNav);
  }, [activePage?.index, offset, direction]);

  var animationX =
    direction === 'left'
      ? { x: '50%' }
      : direction === 'bottom'
        ? { y: '-50%' }
        : { x: '-50%' };

  return (
    <AnimatePresence>
      {(show || defaultNav) && (
        <motion.div
          className={cx(styles.arrow, styles[direction])}
          transition={transition}
          initial={{
            opacity: 0,
            ...animationX
          }}
          animate={{
            opacity: disabled && !defaultNav ? 0 : 1,
            x: 0,
            y: 0
          }}
          exit={{
            opacity: 0,
            ...animationX
          }}
        >
          <button onClick={onClick}>
            <i className={cx('cleverstory-drop-down', styles.arrowIcon)} />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

const Arrow = React.memo(
  ArrowComponent,
  memoFunctionEqual([
    'direction',
    'show',
    'offset',
    'disabled',
    'onClick',
    'activePage'
  ])
)

/**
 * Menu Component
 */
const MenuComponent = ({ pages, offset, color, active, onChange }) => {
  const containerRef = React.useRef()
  const gatingContext = React.useContext(GatingContext)

  const [state, setState] = React.useState({
    show: false,
    activePage: 0,
    hoverPage: 0,
    animate: 'hidden'
  })

  const total = size(pages)
  const progress = ((state.activePage + 1) / Math.max(total, 1)) * 100
  const hasPrevious = state.activePage <= 0
  const hasNext = state.activePage === total - 1

  const headingDelay = Math.min(Math.min(total, 5) * 0.3, 0.8)
  const descriptionDelay = headingDelay + 0.2
  const paginationDelay = descriptionDelay

  const heading = get(pages, `${state.hoverPage}.name`)
  const description = get(pages, `${state.hoverPage}.description`)
  const iconUrl = get(pages, `${state.hoverPage}.icon`)

  React.useEffect(() => {
    setState((state) => {
      return {
        ...state,
        activePage: active,
        hoverPage: active
      }
    })
  }, [active])

  React.useEffect(() => {
    if (state.show && containerRef.current) {
      containerRef.current.focus()
    }

    setState((state) => {
      return {
        ...state,
        animate: state.show ? 'show' : 'hidden'
      }
    })
  }, [state.show])

  const menuVariant = {
    hidden: { y: -200, opacity: 0 },
    show: { y: 0, opacity: 1 },
    changePage: { y: 0, opacity: 1 }
  }

  const hiddenMenuVariant = {
    hidden: { y: 0, opacity: 1 },
    show: { y: 0, opacity: 1 },
    changePage: { y: 0, opacity: 1 }
  }

  const headingVariant = {
    hidden: { y: 40, opacity: 0 },
    show: {
      y: 0,
      opacity: 1,
      transition: {
        ease: 'easeOut',
        delay: headingDelay,
        duration: 0.6
      }
    },
    changePage: {
      y: 0,
      opacity: 1,
      transition: {
        ease: 'easeOut',
        delay: 0.1,
        duration: 1
      }
    }
  }

  const descriptionVariant = {
    hidden: { y: 40, opacity: 0 },
    show: {
      y: 0,
      opacity: 1,
      transition: {
        ease: 'easeOut',
        delay: descriptionDelay,
        duration: 0.6
      }
    },
    changePage: {
      y: 0,
      opacity: 1,
      transition: {
        ease: 'easeOut',
        delay: 0.5,
        duration: 1
      }
    }
  }

  const paginationVariant = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        ease: 'easeOut',
        delay: paginationDelay
      }
    },
    changePage: {
      opacity: 1
    }
  }

  const handleMenuClick = () => {
    setState((state) => {
      return {
        ...state,
        show: true,
        animate: 'show'
      }
    })
  }

  const moveToPage = (page) => {
    onChange(page)
  }

  const handleNext = (e) => {
    e.preventDefault()
    moveToPage(state.activePage + 1)
  }

  const handlePrevious = (e) => {
    e.preventDefault()
    moveToPage(state.activePage - 1)
  }

  const handleClick = (page) => {
    moveToPage(page)
    handleClose()
  }

  const handleMouseOver = (page) => {
    setState((state) => {
      return {
        ...state,
        hoverPage: page,
        animate: 'changePage'
      }
    })
  }

  const handleMouseOut = (page) => {
    setState((state) => {
      return {
        ...state,
        hoverPage: state.activePage,
        animate: 'changePage'
      }
    })
  }

  const handleClose = (e) => {
    if (e) e.preventDefault()
    setState((state) => {
      return {
        ...state,
        show: false
      }
    })
  }
  // Only listen if menu is open
  useEffect(() => {
    if (!state.show) return;

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        handlePrevious(e);
      }
      else if (e.key === 'ArrowRight') {
        handleNext(e);
      }
      else if (e.key === 'Escape' || e.key === 'x' || e.key === 'X') {
        handleClose(e);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [state.show, state.activePage, onChange]);

  return (
    <React.Fragment>
      <motion.div
        className={styles.menuButton}
        animate={{
          transform: `translate3D(0, -${offset}px, 0)`
        }}
        transition={transition}
      >
        <button onClick={handleMenuClick}>
          <i
            style={{ color: colorToString(color) }}
            className={cx('cleverstory-menu', styles.arrowIcon)}
          />
        </button>
      </motion.div>
      <div
        ref={containerRef}
        className={cx(styles.menu, { [styles.show]: state.show })}
        tabIndex="-1"
      >
        <div className={styles.closeButton} onClick={handleClose}>
          <svg className={styles.closeIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <div className={styles.overlay}>
          {iconUrl && <IKImage path={iconUrl} />}
        </div>
        <motion.div
          className={styles.inner}
          key={`container_${state.show ? 'visible' : 'hidden'}`}
          initial='hidden'
          animate={state.animate}
        >
          <motion.div
            className={styles.pageDetails}
            key={`heading_${state.hoverPage}`}
            variants={headingVariant}
          >
            <div className={styles.title}>
              <span>{heading}</span>
            </div>
            <motion.div
              className={styles.description}
              key={`description_${state.hoverPage}`}
              variants={descriptionVariant}
            >
              <p>{description}</p>
            </motion.div>
          </motion.div>

          <motion.div
            className={styles.thumbnails}
            variants={{
              hidden: {},
              show: {
                transition: {
                  staggerChildren: 0.3
                }
              },
              changePage: {}
            }}
            initial={{
              x: -state.activePage * 215
            }}
            animate={{
              x: -state.activePage * 215
            }}
          >
            {pages.map((page, index) => {
              const isActive = state.activePage === index
              const hidden = index - state.activePage < -1

              return (
                <motion.div
                  key={index}
                  className={cx(styles.thumbnail, {
                    [styles.active]: isActive
                  })}
                  variants={hidden ? hiddenMenuVariant : menuVariant}
                  transition={{
                    ease: 'easeOut',
                    bounce: 0,
                    duration: 0.6
                  }}
                  onClick={() => handleClick(index)}
                  onMouseOver={() => handleMouseOver(index)}
                  onMouseOut={() => handleMouseOut()}
                >
                  {page.icon && (
                    <IKImage path={page.icon} width={200} height={130} />
                  )}
                </motion.div>
              )
            })}
          </motion.div>
          <motion.div
            className={styles.pagination}
            variants={paginationVariant}
          >
            <button
              className={cx(styles.arrow, styles.left)}
              disabled={hasPrevious}
              onClick={handlePrevious}
            >
              <ReactSVG
                src={ArrowIcon}
                style={{
                  width: '100%',
                  height: '100%'
                }}
              />
            </button>
            <button
              className={cx(styles.arrow, styles.right)}
              disabled={hasNext}
              onClick={handleNext}
            >
              <ReactSVG
                src={ArrowIcon}
                style={{
                  width: '100%',
                  height: '100%'
                }}
              />
            </button>
            <div className={styles.scrollbar}>
              <motion.div
                className={styles.thumb}
                animate={{
                  width: `${progress}%`
                }}
              />
              <div className={styles.bar} />
            </div>
          </motion.div>
        </motion.div>
        <motion.div className={styles.escapeText} variants={paginationVariant}>
          Press <span>esc</span> to head back
        </motion.div>
      </div>
    </React.Fragment>
  )
}

const Menu = React.memo(
  MenuComponent,
  memoFunctionEqual(['pages', 'offset', 'color', 'active', 'onChange'])
)

/*
 * Background manager
 */
function BackgroundManager({ background }) {
  if (!background) {
    return (
      <div
        style={{ background: '#ffffff' }}
        className={styles.customBackground}
      />
    )
  }

  switch (background.type) {
    case 'color': {
      return (
        <div
          style={{ background: colorToString(background.color) }}
          className={styles.customBackground}
        />
      )
    }

    case 'image': {
      if (get(background, 'media.type') === 'image') {
        return (
          <div className={styles.customBackground}>
            <ImageView background={background} />
          </div>
        )
      }

      return (
        <div className={styles.customBackground}>
          <VideoView background={background} />
        </div>
      )
    }

    default:
      return (
        <div
          style={{ background: '#ffffff' }}
          className={styles.customBackground}
        />
      )
  }
}

/*
 * Key Manager
 */
function KeyManager({ children }) {
  const containerRef = React.useRef()
  const carouselContext = React.useContext(CarouselContext)
  const gatingContext = React.useContext(GatingContext)

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.focus()
    }
  }, [])

  const navigateNext = React.useCallback(
    (e) => {
      e.preventDefault()

      if (gatingContext.configuration) {
        return
      }

      carouselContext.navigateToPage(
        carouselContext.activePage.index + 1,
        carouselContext.activePage.fullscreen
      )
    },
    [carouselContext.activePage]
  )

  const navigatePrevious = React.useCallback(
    (e) => {
      e.preventDefault()

      if (gatingContext.configuration) {
        return
      }

      carouselContext.navigateToPage(
        carouselContext.activePage.index - 1,
        carouselContext.activePage.fullscreen
      )
    },
    [carouselContext.activePage]
  )

  const toggleFullScreen = React.useCallback(
    (e) => { },
    [carouselContext.activePage]
  )

  useEffect(() => {
    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'ArrowLeft':
          navigatePrevious(e);
          break;
        case 'ArrowRight':
          navigateNext(e);
          break;
        case 'ArrowUp':
        case 'ArrowDown':
          toggleFullScreen(e);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigateNext, navigatePrevious, toggleFullScreen]);

  return (
    <div className={styles.keymanager}>
      {children}
    </div>
  )
}

function colorToString(color) {

  if (!color || !has(color, 'r')) {
    return 'rgba(0,0,0,1)'
  }

  return `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`
}

function SearchKeyManager({ children, toggleShowSeach }) {
  const gatingContext = React.useContext(GatingContext)

  const keyMap = {
    SEARCH: ['ctrl+f', 'meta+f'],
    ESC: 'Escape'
  }

  const handleShowSearch = (e) => {
    e.preventDefault()

    if (gatingContext.configuration) {
      return
    }

    toggleShowSeach(true)
  }

  const handleHideSearch = (e) => {
    e.preventDefault()

    if (gatingContext.configuration) {
      return
    }

    toggleShowSeach(false)
  }
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'f' || e.key === 'F')) {
        handleShowSearch(e);
      } 
      else if (e.key === 'Escape') {
        handleHideSearch(e);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleShowSearch, handleHideSearch]);

  return (
    <div className={styles.keymanager}>
      {children}
    </div>
  )
}



const FullScreenView = ({ onFullScreen, fullScreen, iconColor }) => {
  return (
    <button onClick={() => onFullScreen()} className={styles.fullscreenButton}>
      {!fullScreen && (
        <i
          style={{ color: colorToString(iconColor) }}
          className={cx(styles.searchIcon, 'cleverstory-expand-editor')}
        />
      )}
      {fullScreen && (
        <i
          style={{ color: colorToString(iconColor) }}
          className={cx(styles.searchIcon, 'cleverstory-minimize-editor')}
        />
      )}
    </button>
  )
}

const SearchPopup = ({ iconColor }) => {
  const carouselContext = React.useContext(CarouselContext)

  const searchInputRef = React.useRef(null)

  const [showSearch, setShowSearch] = React.useState(false)
  const [searchContext, setSearchContext] = useState({
    query: '',
    index: 0,
    total: 0,
    processed: false,
    navigating: false,
    markInstance: null
  })

  React.useEffect(() => {
    if (showSearch && searchInputRef.current) {
      searchInputRef.current.focus()
    }

    if (!showSearch) {
      resetHighlight()
    }
  }, [showSearch])

  const hasNext =
    searchContext.processed &&
    !searchContext.navigating &&
    searchContext.total > 0
  const hasPrevious =
    searchContext.processed &&
    !searchContext.navigating &&
    searchContext.total > 0

  const resetHighlight = () => {
    if (searchContext.markInstance) {
      searchContext.markInstance.unmark()
    }

    setShowSearch(false)
    setSearchContext((state) => {
      return {
        ...state,
        query: '',
        index: 0,
        total: 0,
        processed: false,
        navigating: false
      }
    })
  }

  const handleSearch = async (e) => {
    e.preventDefault()

    if (searchContext.navigating) {
      return
    }

    if (searchContext.processed) {
      nextClick()
      return
    }
    const { default: Mark } = await import('mark.js')

    var options = {
      separateWordSearch: false,
      exclude: ['label', '.backToTop']
    }

    var markInstance = searchContext.markInstance
    if (!markInstance) {
      var textInstances = document.querySelectorAll('.cs_text_widget')
      markInstance = new Mark(textInstances)
    }

    markInstance.unmark({
      done: () => {
        markInstance.mark(searchContext.query, options)
      }
    })

    var results = document.querySelectorAll(
      ".cs_text_widget mark[data-markjs='true']"
    ).length // total length
    setSearchContext((state) => {
      return {
        ...state,
        processed: true,
        navigating: false,
        index: 0,
        total: results,
        markInstance: markInstance
      }
    })

    if (results > 0) {
      navigate(0)
    }
  }

  const previousClick = () => {
    var previous = searchContext.index - 1
    if (previous < 0) {
      previous = searchContext.total - 1
    }
    if (previous > searchContext.total) {
      previous = 0
    }

    navigate(previous)
  }

  const nextClick = () => {
    var next = searchContext.index + 1
    if (next < 0) {
      next = 0
    }

    if (next >= searchContext.total) {
      next = 0
    }

    navigate(next)
  }

  const updateNavigationStatus = (navigating, index) => {
    setSearchContext((state) => {
      return {
        ...state,
        navigating: navigating,
        index: index
      }
    })
  }

  const navigate = (matchIndex) => {
    const elements = document.querySelectorAll(
      `.cs_text_widget mark[data-markjs='true']`
    )
    var highlightElement = null
    if (elements.length > 0) {
      highlightElement = elements[matchIndex]
    }

    document.querySelectorAll('.cs_search__highlight').forEach((elem) => {
      elem.classList.remove('cs_search__highlight')
    })

    if (highlightElement) {
      highlightElement.classList.add('cs_search__highlight')
      updateNavigationStatus(true, matchIndex)

      const toScrollItem = highlightElement.closest(
        '.interactive_content__page'
      )
      var id = toScrollItem.id
      id = id.split('_')
      var index = id[1]
      index = parseInt(index < 0 ? 0 : index)

      carouselContext.navigateToPage(index, true).then(() => {
        if (!isSelectionVisible(highlightElement)) {
          highlightElement.scrollIntoView()

          setTimeout(() => {
            document.querySelector(`.${styles.carousel}`).scrollTop = 0
          }, 100)
        }

        updateNavigationStatus(false, matchIndex)
      })
    }
  }

  const isSelectionVisible = (element) => {
    var rect = element.getBoundingClientRect()

    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <=
      (window.innerHeight ||
        document.documentElement.clientHeight) /* or $(window).height() */ &&
      rect.right <=
      (window.innerWidth ||
        document.documentElement.clientWidth) /* or $(window).width() */
    )
  }

  const popover = (
    <Popover className={styles.searchPopover}>
      <Popover.Body className={styles.searchPopoverBody}>
        <div className={styles.findInPage}>
          <div className={styles.searchBox}>
            <form onSubmit={handleSearch}>
              <input
                ref={searchInputRef}
                onChange={(e) => {
                  setSearchContext((state) => {
                    return {
                      ...state,
                      query: e.target.value,
                      processed: false
                    }
                  })
                }}
              />
            </form>
            {searchContext.processed && (
              <span>
                {searchContext.total == 0 ? 0 : searchContext.index + 1}/
                {searchContext.total}
              </span>
            )}
          </div>
          <div className={styles.searchActions}>
            <button data-search='next' disabled={!hasNext} onClick={nextClick}>
              <ReactSVG
                src={DownArrowIcon}
                style={{
                  width: '100%',
                  height: '100%'
                }}
              />
            </button>
            <button
              data-search='prev'
              disabled={!hasPrevious}
              onClick={previousClick}
            >
              <ReactSVG
                src={UpArrowIcon}
                style={{
                  width: '100%',
                  height: '100%'
                }}
              />
            </button>
            <button
              data-search='cancel'
              onClick={() => {
                setShowSearch(false)
              }}
            >
              <i className={cx(styles.navigationIcon, 'cleverstory-close')} />
            </button>
          </div>
        </div>
      </Popover.Body>
    </Popover>
  )

  return (
    <SearchKeyManager toggleShowSeach={setShowSearch}>
      <OverlayTrigger overlay={popover} show={showSearch}>
        <div
          className={styles.searchButton}
          onClick={() => {
            setShowSearch(true)
          }}
        >
          <i
            style={{ color: colorToString(iconColor) }}
            className={cx(styles.searchIcon, 'cleverstory-search')}
          />
        </div>
      </OverlayTrigger>
    </SearchKeyManager>
  )
}